/**
 * The assistant panel: a drawer on the side of every screen. A thread of messages, what the
 * assistant did (tool chips), the proposals it made (open the pre-filled form, open a screen), the
 * recent conversations, and the context it was opened from.
 */
import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api.ts';
import { useQuery } from '../lib/hooks.ts';
import { useI18n } from '../lib/i18n.tsx';
import { useMeta } from '../lib/meta.tsx';
import { useRoute } from '../lib/router.tsx';
import { stashProposal, useAi, type ChatReply, type Proposal } from '../lib/ai.tsx';
import { Button, IconButton, useToast } from './ui.tsx';
import { Icon } from './Icon.tsx';
import type { Row } from '../lib/types.ts';

interface Msg { role: 'user' | 'assistant'; content: string; toolCalls?: ChatReply['toolCalls']; proposals?: Proposal[]; pending?: boolean }

export function AssistantPanel() {
  const { t } = useI18n();
  const ai = useAi();
  const { entities } = useMeta();
  const { navigate } = useRoute();
  const toast = useToast();
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [showList, setShowList] = useState(false);
  const list = useQuery(ai.open && showList ? 'ai:convs' : null, () => api.get<Row[]>('ai/conversations'), { ttlMs: 0 });
  const bottom = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { if (ai.open) { setText((s) => s || ai.prefill); setTimeout(() => input.current?.focus(), 50); } }, [ai.open, ai.prefill]);
  useEffect(() => { bottom.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  if (!ai.open) return null;

  const send = async (msg?: string) => {
    const message = (msg ?? text).trim();
    if (!message || busy) return;
    setText(''); setBusy(true);
    setMessages((m) => [...m, { role: 'user', content: message }, { role: 'assistant', content: '', pending: true }]);
    try {
      const r = await ai.chat(message, conversationId, ai.context ?? undefined);
      setConversationId(r.conversationId);
      setMessages((m) => [...m.slice(0, -1), { role: 'assistant', content: r.reply, toolCalls: r.toolCalls, proposals: r.proposals }]);
    } catch (e) {
      setMessages((m) => m.slice(0, -1));
      toast.error(e);
    } finally { setBusy(false); }
  };
  const load = async (id: string) => {
    try {
      const rows = await api.get<Row[]>(`ai/conversations/${id}`);
      setConversationId(id); setShowList(false);
      setMessages(rows.map((r) => ({ role: r.role as 'user' | 'assistant', content: String(r.content), toolCalls: (r.tool_calls as ChatReply['toolCalls']) ?? [], proposals: (r.proposals as Proposal[]) ?? [] })).filter((m) => m.role !== ('tool' as string)));
    } catch (e) { toast.error(e); }
  };
  const apply = (p: Proposal) => {
    if (p.kind === 'screen' && p.path) { navigate(p.path); ai.closeAssistant(); return; }
    const def = p.entity ? entities.get(p.entity) : null;
    if (!def) return;
    stashProposal(p);
    navigate(`${def.route}/new?ai=1`);
    ai.closeAssistant();
  };
  const starters = ai.context?.entity && ai.context.recordId
    ? [t('ai.starterSummarise'), t('ai.starterNext'), t('ai.starterRisks')]
    : [t('ai.starterOverdue'), t('ai.starterStock'), t('ai.starterProjects')];

  return (
    <aside className="assistant" data-assistant>
      <header className="assistant-h">
        <Icon name="Sparkles" size={18} /><b>{t('ai.assistant')}</b>
        {ai.context?.title && <span className="chip" title={ai.context.path}>{ai.context.title}</span>}
        <span className="spacer" />
        <IconButton icon="History" label={t('ai.conversations')} onClick={() => setShowList((s) => !s)} />
        <IconButton icon="Plus" label={t('ai.newConversation')} onClick={() => { setConversationId(null); setMessages([]); setShowList(false); }} />
        <IconButton icon="X" label={t('common.close')} onClick={ai.closeAssistant} />
      </header>
      {showList ? (
        <div className="assistant-list">
          {!list.data ? <div className="muted">{t('common.loading')}</div> : list.data.length === 0 ? <div className="muted">{t('ai.noConversations')}</div> : list.data.map((c) => (
            <button type="button" key={String(c.id)} className="assistant-conv" onClick={() => void load(String(c.id))}><b>{String(c.title ?? '')}</b><span className="muted">{String(c.messages)} · {new Date(String(c.updated_at)).toLocaleDateString()}</span></button>
          ))}
        </div>
      ) : (
        <div className="assistant-body">
          {ai.status?.mode === 'mock' && <div className="banner amber" style={{ fontSize: 12 }}>{t('ai.mockNote')}</div>}
          {messages.length === 0 && (
            <div className="assistant-empty">
              <p className="muted">{t('ai.intro')}</p>
              <div className="chips">{starters.map((s) => <button type="button" key={s} className="chip" onClick={() => void send(s)}>{s}</button>)}</div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`msg from-${m.role}`} data-role={m.role}>
              {m.pending ? <span className="muted"><Icon name="LoaderCircle" className="spin" size={14} /> {t('ai.thinking')}</span> : <div className="msg-text">{m.content}</div>}
              {!!m.toolCalls?.length && <div className="tool-chips">{m.toolCalls.map((c, j) => <span key={j} className={`chip ${c.ok ? '' : 'err'}`} title={JSON.stringify(c.input)}><Icon name={c.ok ? 'Check' : 'X'} size={11} /> {c.name}</span>)}</div>}
              {!!m.proposals?.length && (
                <div className="proposals">
                  {m.proposals.map((p, j) => (
                    <div key={j} className="proposal" data-proposal={p.kind}>
                      <div><b>{p.label}</b>{p.reason && <div className="muted" style={{ fontSize: 12 }}>{p.reason}</div>}
                        {p.kind === 'record' && <div className="muted" style={{ fontSize: 12 }}>{Object.entries(p.fields ?? {}).slice(0, 6).map(([k, v]) => `${k}: ${String(v)}`).join(' · ')}{p.lines?.length ? ` · ${p.lines.length} ${t('ai.lines')}` : ''}</div>}
                        {!!p.issues?.length && <div className="muted" style={{ fontSize: 12, color: 'var(--amber)' }}>{p.issues.map((x) => x.message).join('; ')}</div>}
                      </div>
                      <Button size="sm" variant="primary" icon={p.kind === 'record' ? 'FilePlus' : 'ArrowRight'} onClick={() => apply(p)}>{p.kind === 'record' ? t('ai.openDraft') : t('ai.openScreen')}</Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div ref={bottom} />
        </div>
      )}
      <footer className="assistant-f">
        <textarea ref={input} className="input" rows={2} value={text} placeholder={t('ai.placeholder')} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); } }} data-assistant-input />
        <Button variant="primary" icon="Send" busy={busy} onClick={() => void send()} data-assistant-send>{t('ai.send')}</Button>
      </footer>
    </aside>
  );
}
